import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PublicHeader from "@/components/PublicHeader";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Save, Plus, X } from "lucide-react";

export default function CandidateProfile() {
  const [u, setU] = useState(null);
  const [skill, setSkill] = useState("");
  const nav = useNavigate();
  useEffect(()=>{ api.get("/candidate/profile").then(r=>setU(r.data)); },[]);
  if (!u) return <div><PublicHeader/><div className="p-10 text-slate-500">Loading...</div></div>;
  const p = u.profile || {};
  const setP = (k,v) => setU({...u, profile: {...p, [k]: v}});
  const addSkill = () => { if(!skill.trim()) return; setP("skills",[...(p.skills||[]), skill.trim()]); setSkill(""); };
  const removeSkill = (s) => setP("skills",(p.skills||[]).filter(x=>x!==s));
  const save = async () => {
    try {
      await api.put("/candidate/profile", {
        full_name: u.full_name, phone: u.phone,
        location: p.location, linkedin: p.linkedin, portfolio: p.portfolio, github: p.github,
        current_role: p.current_role, current_company: p.current_company,
        experience_years: p.experience_years, notice_period: p.notice_period,
        expected_ctc: p.expected_ctc, skills: p.skills, education: p.education, experience: p.experience,
        resume_url: p.resume_url, resume_filename: p.resume_filename,
      });
      toast.success("Profile saved");
    } catch(e){ toast.error("Failed to save"); }
  };
  const addEdu = () => setP("education",[...(p.education||[]),{degree:"",institution:"",year:""}]);
  const addExp = () => setP("experience",[...(p.experience||[]),{company:"",role:"",dates:"",description:""}]);
  return (
    <div className="min-h-screen bg-slate-50">
      <PublicHeader/>
      <div className="max-w-5xl mx-auto p-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="font-display text-3xl font-bold text-[#0A2540]">My Profile</h1>
          <Button onClick={save} className="bg-[#0A2540] hover:bg-[#0A2540]/90" data-testid="profile-save"><Save className="w-4 h-4 mr-2"/>Save</Button>
        </div>
        <Card className="p-6 mb-6 border-slate-200">
          <h2 className="font-semibold text-[#0A2540] mb-4">Personal</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div><Label>Full name</Label><Input value={u.full_name||""} onChange={e=>setU({...u, full_name:e.target.value})} data-testid="p-fullname"/></div>
            <div><Label>Phone</Label><Input value={u.phone||""} onChange={e=>setU({...u, phone:e.target.value})} data-testid="p-phone"/></div>
            <div><Label>Location</Label><Input value={p.location||""} onChange={e=>setP("location",e.target.value)} data-testid="p-location"/></div>
            <div><Label>LinkedIn</Label><Input value={p.linkedin||""} onChange={e=>setP("linkedin",e.target.value)}/></div>
            <div><Label>Portfolio</Label><Input value={p.portfolio||""} onChange={e=>setP("portfolio",e.target.value)}/></div>
            <div><Label>GitHub</Label><Input value={p.github||""} onChange={e=>setP("github",e.target.value)}/></div>
          </div>
        </Card>
        <Card className="p-6 mb-6 border-slate-200">
          <h2 className="font-semibold text-[#0A2540] mb-4">Professional</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div><Label>Current role</Label><Input value={p.current_role||""} onChange={e=>setP("current_role",e.target.value)}/></div>
            <div><Label>Current company</Label><Input value={p.current_company||""} onChange={e=>setP("current_company",e.target.value)}/></div>
            <div><Label>Experience (years)</Label><Input type="number" value={p.experience_years||""} onChange={e=>setP("experience_years",Number(e.target.value))}/></div>
            <div><Label>Notice period</Label><Input value={p.notice_period||""} onChange={e=>setP("notice_period",e.target.value)}/></div>
            <div><Label>Expected CTC</Label><Input value={p.expected_ctc||""} onChange={e=>setP("expected_ctc",e.target.value)}/></div>
            <div><Label>Resume URL</Label><Input value={p.resume_url||""} onChange={e=>setP("resume_url",e.target.value)} placeholder="Paste your resume link" data-testid="p-resume"/></div>
          </div>
        </Card>
        <Card className="p-6 mb-6 border-slate-200">
          <h2 className="font-semibold text-[#0A2540] mb-4">Skills</h2>
          <div className="flex gap-2 mb-3">
            <Input placeholder="Add a skill (e.g. Python)" value={skill} onChange={e=>setSkill(e.target.value)} onKeyDown={e=>e.key==="Enter"&&(e.preventDefault(),addSkill())} data-testid="p-skill-input"/>
            <Button onClick={addSkill} data-testid="p-skill-add"><Plus className="w-4 h-4"/></Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {(p.skills||[]).map(s=>(
              <Badge key={s} className="bg-blue-100 text-blue-800 border-none hover:bg-blue-100 cursor-pointer" onClick={()=>removeSkill(s)}>{s} <X className="w-3 h-3 ml-1"/></Badge>
            ))}
          </div>
        </Card>
        <Card className="p-6 mb-6 border-slate-200">
          <div className="flex justify-between items-center mb-4"><h2 className="font-semibold text-[#0A2540]">Education</h2><Button size="sm" variant="outline" onClick={addEdu}><Plus className="w-4 h-4 mr-1"/>Add</Button></div>
          <div className="space-y-3">
            {(p.education||[]).map((e,i)=>(
              <div key={i} className="grid md:grid-cols-3 gap-3 border border-slate-200 rounded-md p-3">
                <Input placeholder="Degree" value={e.degree} onChange={ev=>{const arr=[...p.education]; arr[i].degree=ev.target.value; setP("education",arr);}}/>
                <Input placeholder="Institution" value={e.institution} onChange={ev=>{const arr=[...p.education]; arr[i].institution=ev.target.value; setP("education",arr);}}/>
                <Input placeholder="Year" value={e.year} onChange={ev=>{const arr=[...p.education]; arr[i].year=ev.target.value; setP("education",arr);}}/>
              </div>
            ))}
          </div>
        </Card>
        <Card className="p-6 mb-6 border-slate-200">
          <div className="flex justify-between items-center mb-4"><h2 className="font-semibold text-[#0A2540]">Experience</h2><Button size="sm" variant="outline" onClick={addExp}><Plus className="w-4 h-4 mr-1"/>Add</Button></div>
          <div className="space-y-3">
            {(p.experience||[]).map((x,i)=>(
              <div key={i} className="border border-slate-200 rounded-md p-3 grid md:grid-cols-2 gap-3">
                <Input placeholder="Company" value={x.company} onChange={ev=>{const arr=[...p.experience]; arr[i].company=ev.target.value; setP("experience",arr);}}/>
                <Input placeholder="Role" value={x.role} onChange={ev=>{const arr=[...p.experience]; arr[i].role=ev.target.value; setP("experience",arr);}}/>
                <Input placeholder="Dates" value={x.dates} onChange={ev=>{const arr=[...p.experience]; arr[i].dates=ev.target.value; setP("experience",arr);}}/>
                <Textarea placeholder="Description" value={x.description} onChange={ev=>{const arr=[...p.experience]; arr[i].description=ev.target.value; setP("experience",arr);}}/>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
